import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import Config from "../../Constants/Config";
import {
  EVENT_LABEL,
  errorCode,
  fmtClock,
  lumanaVideoVisibleFor,
  markerFraction,
  momentCashier,
  readError,
  useLumanaApi,
} from "./lumanaApi";
import { useAuthDetails } from "../../Common/cookiesHelper";
import "./OrderVideo.css";

/**
 * Camera footage on an order (Lumana). Renders nothing unless the store is
 * connected and the session may see video — the server decides both
 * (POST Lumana/video/order answers connected:false, 403 or 404).
 */

const momentLabel = (m) => (m.type === "REFUND" ? "Refund" : EVENT_LABEL[m.type] || m.type);

const togglePlay = (v) => {
  if (!v) return;
  if (v.paused) v.play().catch(() => {});
  else v.pause();
};

const Play = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5.5v13a.6.6 0 00.9.5l10.3-6.5a.6.6 0 000-1L8.9 5a.6.6 0 00-.9.5z" fill="currentColor" />
  </svg>
);

/* ------------------------------------------------------------------------ */
/*                                 Player                                   */
/* ------------------------------------------------------------------------ */

const OrderVideoPlayer = ({ data, initialKey, onClose }) => {
  const post = useLumanaApi();
  const tz = data.order.timezone;
  const [momentKey, setMomentKey] = useState(initialKey);
  const moment = data.moments.find((m) => m.key === momentKey) || data.moments[0];
  const cashier = momentCashier(moment, data.order);
  const [cameraId, setCameraId] = useState(moment?.cameras?.[0]?.camera_id || "");
  const [extend, setExtend] = useState({ pre: 0, post: 0 });
  const [phase, setPhase] = useState("starting");
  const [problem, setProblem] = useState(null);
  const [pb, setPb] = useState(null);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const sessions = useRef({});
  const dialogRef = useRef(null);
  // One automatic restart per clip: Lumana closes a session after 90 s without
  // a fetch, which a paused viewer hits; the same session restarts where they were.
  const autoRetried = useRef(false);
  const resumeAt = useRef(0);
  const clipRef = useRef("");

  const windows = data.clip_windows?.[moment?.type] || { pre: 60, post: 15 };
  const preSec = windows.pre + extend.pre;
  const postSec = windows.post + extend.post;
  const clip = `${momentKey}|${cameraId}|${preSec}|${postSec}`;

  const stopAll = useCallback(() => {
    hlsRef.current?.destroy?.();
    hlsRef.current = null;
    for (const [cam, sid] of Object.entries(sessions.current)) {
      post(Config.LUMANA_VIDEO_STOP, { camera_id: cam, session_id: sid }).catch(() => {});
    }
    sessions.current = {};
  }, [post]);

  const close = useCallback(() => {
    stopAll();
    onClose();
  }, [stopAll, onClose]);

  // Moment switch → keep the camera if this moment offers it, else its first.
  useEffect(() => {
    if (!moment) return;
    if (!moment.cameras.some((c) => c.camera_id === cameraId)) setCameraId(moment.cameras[0]?.camera_id || "");
    setExtend((x) => (x.pre || x.post ? { pre: 0, post: 0 } : x));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [momentKey]);

  useEffect(() => {
    if (!moment || !cameraId) return undefined;
    let cancelled = false;
    if (clipRef.current !== clip) {
      clipRef.current = clip;
      autoRetried.current = false;
      resumeAt.current = 0;
    }
    setPhase("starting");
    setProblem(null);
    setEndedEarly(false);
    setT(0);
    hlsRef.current?.destroy?.();
    hlsRef.current = null;

    (async () => {
      try {
        const res = await post(Config.LUMANA_VIDEO_START, {
          order_id: data.order.order_id,
          moment_key: moment.key,
          camera_id: cameraId,
          session_id: sessions.current[cameraId],
          pre_sec: preSec,
          post_sec: postSec,
        });
        if (cancelled) return;
        const p = res.playback;
        sessions.current[cameraId] = p.session_id;
        setPb(p);
        setDur((p.end_ms - p.start_ms) / 1000);
        const video = videoRef.current;
        if (!video) return;

        const { default: Hls } = await import("hls.js");
        if (cancelled) return;
        if (Hls.isSupported()) {
          let started = false;
          const hls = new Hls({
            maxBufferLength: 30,
            startPosition: resumeAt.current,
            xhrSetup: (xhr) => {
              // Lumana reads the playback token raw from Authorization — no Bearer (2026-10-15).
              if (p.token) xhr.setRequestHeader("Authorization", p.token);
            },
          });
          resumeAt.current = 0;
          hlsRef.current = hls;
          // Attach first and load the playlist once the MediaSource is open;
          // playing (or loading) before that leaves the MediaSource closed.
          hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(p.url));
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            started = true;
            setPhase("ready");
          });
          hls.on(Hls.Events.ERROR, (_e, d) => {
            if (!d?.fatal) return;
            if (started && !autoRetried.current) {
              autoRetried.current = true;
              resumeAt.current = video.currentTime || 0;
              setAttempt((a) => a + 1);
              return;
            }
            setPhase("error");
            setProblem({ code: "player", text: "The footage could not be played. Lumana may still be preparing it." });
          });
          video.addEventListener("canplay", () => video.play().catch(() => {}), { once: true });
          hls.attachMedia(video);
        } else {
          // A native HLS player (older Safari) cannot send the token header.
          setPhase("error");
          setProblem({ code: "browser", text: "This browser cannot play Lumana footage. Use Chrome, Edge or Firefox." });
        }
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setProblem({ code: errorCode(err), text: readError(err, "Could not start the video.") });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [momentKey, cameraId, preSec, postSec, attempt]);

  useEffect(() => () => stopAll(), [stopAll]);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === " " && e.target === dialogRef.current) {
        e.preventDefault();
        togglePlay(videoRef.current);
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [close]);

  const span = pb ? (pb.end_ms - pb.start_ms) / 1000 : 0;
  const eventFrac = pb ? markerFraction(pb.event_at_ms, pb.start_ms, pb.end_ms) : 0;
  const headFrac = span > 0 ? Math.min(1, t / span) : 0;
  const wall = pb ? pb.start_ms + t * 1000 : null;

  const seek = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(sec, (v.duration || span) - 0.2));
  };
  const jumpToEvent = () => pb && seek((pb.event_at_ms - pb.start_ms) / 1000 - 2);
  const onTrack = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    seek(((e.clientX - r.left) / r.width) * span);
  };

  const camera = moment?.cameras.find((c) => c.camera_id === cameraId);

  return createPortal(
    <div className="qvlv-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="qvlv-theater" role="dialog" aria-modal="true" aria-label={`Video for order ${data.order.order_id}`} tabIndex={-1} ref={dialogRef}>
        <header className="qvlv-theater-head">
          <div className="qvlv-moments" role="tablist">
            {data.moments.map((m) => (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={m.key === momentKey}
                className={`qvlv-mtab qvlv-mtab--${m.type.toLowerCase()} ${m.key === momentKey ? "is-on" : ""}`}
                onClick={() => setMomentKey(m.key)}
              >
                <span>{momentLabel(m)}</span>
                <small>{fmtClock(m.at_ms, tz)}</small>
              </button>
            ))}
          </div>
          <button type="button" className="qvlv-x" onClick={close} aria-label="Close video">
            ×
          </button>
        </header>

        <div className="qvlv-stage">
          <div className="qvlv-screen">
            <video ref={videoRef} muted playsInline onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onDurationChange={(e) => Number.isFinite(e.currentTarget.duration) && setDur(e.currentTarget.duration)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={(e) => {
              setPlaying(false);
              if (span && e.currentTarget.currentTime < span - 2) setEndedEarly(true);
            }} />
            <div className="qvlv-osd">
              <span className="qvlv-osd-cam">
                <i /> {camera?.camera_name || "Camera"}
              </span>
              {wall !== null && <span className="qvlv-osd-time">{fmtClock(wall, tz)}</span>}
            </div>
            {phase === "starting" && (
              <div className="qvlv-cover">
                <span className="qvlv-spinner" />
                Pulling footage from the store recorder…
              </div>
            )}
            {phase === "error" && (
              <div className={`qvlv-cover qvlv-cover--err qvlv-cover--${problem?.code || "x"}`}>
                <b>
                  {problem?.code === "no_footage"
                    ? "No recording for this time"
                    : problem?.code === "recorder_offline"
                      ? "Store recorder is offline"
                      : problem?.code === "key_rejected"
                        ? "Lumana key needs replacing"
                        : problem?.code === "too_many_viewers"
                          ? "Too many people are watching"
                          : "Video unavailable"}
                </b>
                <span>{problem?.text}</span>
                {problem?.code !== "key_rejected" && (
                  <button
                    type="button"
                    className="qvlv-btn"
                    onClick={() => {
                      autoRetried.current = false;
                      setAttempt((a) => a + 1);
                    }}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}
            {endedEarly && phase === "ready" && <div className="qvlv-early">Recording ends here — the camera has no footage after {fmtClock(wall, tz)}.</div>}
          </div>

          <div className="qvlv-timeline">
            <button type="button" className="qvlv-playbtn" onClick={() => togglePlay(videoRef.current)} aria-label={playing ? "Pause" : "Play"} disabled={phase !== "ready"}>
              {playing ? <span className="qvlv-pause" /> : <Play />}
            </button>
            <div className="qvlv-track" onClick={onTrack} role="slider" aria-label="Clip position" aria-valuemin={0} aria-valuemax={Math.round(span)} aria-valuenow={Math.round(t)} tabIndex={0}>
              <div className="qvlv-track-fill" style={{ width: `${headFrac * 100}%` }} />
              {pb && (
                <span className={`qvlv-marker qvlv-marker--${moment.type.toLowerCase()}`} style={{ left: `${eventFrac * 100}%` }}>
                  <em>
                    {momentLabel(moment)} · {fmtClock(pb.event_at_ms, tz)}
                  </em>
                </span>
              )}
              <span className="qvlv-head" style={{ left: `${headFrac * 100}%` }} />
            </div>
            <div className="qvlv-track-ends">
              <span>{pb ? fmtClock(pb.start_ms, tz) : ""}</span>
              <span>{pb ? fmtClock(pb.end_ms, tz) : ""}</span>
            </div>
            <div className="qvlv-tools">
              <button type="button" className="qvlv-btn qvlv-btn--accent" onClick={jumpToEvent} disabled={phase !== "ready"}>
                Jump to {momentLabel(moment).toLowerCase()}
              </button>
              <button type="button" className="qvlv-btn" onClick={() => setExtend((x) => ({ ...x, pre: Math.min(x.pre + 30, 1800) }))}>
                30s earlier
              </button>
              <button type="button" className="qvlv-btn" onClick={() => setExtend((x) => ({ ...x, post: Math.min(x.post + 30, 1800) }))}>
                30s later
              </button>
              <span className="qvlv-span">
                {Math.round(preSec)}s before · {Math.round(postSec)}s after
                {dur ? ` · ${Math.round(dur)}s clip` : ""}
              </span>
            </div>
          </div>
        </div>

        <aside className="qvlv-slip">
          <div className="qvlv-slip-paper">
            <p className="qvlv-slip-head">{momentLabel(moment)}</p>
            <p className="qvlv-slip-order">#{data.order.order_id}</p>
            <dl>
              <div>
                <dt>Time</dt>
                <dd>{fmtClock(moment.at_ms, tz)}</dd>
              </div>
              <div>
                <dt>Register</dt>
                <dd>{data.order.register}</dd>
              </div>
              {cashier && (
                <div>
                  <dt>Cashier</dt>
                  <dd>{cashier}</dd>
                </div>
              )}
              {moment.reason && (
                <div>
                  <dt>Reason</dt>
                  <dd>{moment.reason}</dd>
                </div>
              )}
            </dl>
            {moment.late_sync && <p className="qvlv-slip-note">Rung up offline and synced later — the clip uses the time it was rung up.</p>}
          </div>
          <div className="qvlv-cams">
            <p>Cameras</p>
            {moment.cameras.map((c) => (
              <button key={c.camera_id} type="button" className={`qvlv-cam ${c.camera_id === cameraId ? "is-on" : ""}`} onClick={() => setCameraId(c.camera_id)}>
                <i />
                <span>{c.camera_name}</span>
                <small>{c.origin === "register" ? "this register" : c.origin === "default" ? "store default" : "other"}</small>
              </button>
            ))}
          </div>
          <p className="qvlv-foot">Footage streams from Lumana and is never stored by Quickvee. Every view is logged.</p>
        </aside>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------------------------------------------------------ */
/*                               Order strip                                */
/* ------------------------------------------------------------------------ */

const OrderVideoStrip = ({ orderId, onPlayerToggle }) => {
  const post = useLumanaApi();
  const { userTypeData, LoginGetDashBoardRecordJson } = useAuthDetails();
  const { permissions = [] } = useSelector((state) => state.permissions || {});
  const allowed = lumanaVideoVisibleFor(LoginGetDashBoardRecordJson?.login_type || userTypeData?.login_type, permissions);
  const [data, setData] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [open, setOpen] = useState(null);
  const openedFrom = useRef(null);

  // A host modal (MUI) must let focus and Escape reach the player while it is open.
  useEffect(() => {
    onPlayerToggle?.(Boolean(open));
    return () => onPlayerToggle?.(false);
  }, [open, onPlayerToggle]);

  useEffect(() => {
    if (!orderId || !allowed) return undefined;
    const ctl = new AbortController();
    (async () => {
      try {
        const res = await post(Config.LUMANA_VIDEO_ORDER, { order_id: orderId }, { signal: ctl.signal });
        if (res?.connected) setData(res);
      } catch {
        /* not connected, not allowed or not found: show nothing */
      }
    })();
    return () => ctl.abort();
  }, [orderId, post, allowed]);

  const playable = useMemo(() => (data?.moments || []).filter((m) => m.cameras.length), [data]);

  useEffect(() => {
    if (!data || data.connection_status === "key_rejected") return undefined;
    let stop = false;
    (async () => {
      for (const m of playable) {
        if (stop) return;
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await post(Config.LUMANA_VIDEO_SNAPSHOT, {
            order_id: data.order.order_id,
            moment_key: m.key,
            camera_id: m.cameras[0].camera_id,
          });
          if (!stop) setThumbs((s) => ({ ...s, [m.key]: res.image }));
        } catch {
          if (!stop) setThumbs((s) => ({ ...s, [m.key]: "" }));
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [data, playable, post]);

  if (!allowed || !data) return null;
  const tz = data.order.timezone;

  return (
    <section className="qvlv-strip" aria-label="Camera footage">
      <div className="qvlv-strip-head">
        <span className="qvlv-strip-title">
          <i className="qvlv-rec" /> Camera footage
        </span>
        <span className="qvlv-strip-meta">{data.order.register}</span>
      </div>
      {data.connection_status === "key_rejected" ? (
        <p className="qvlv-strip-msg">Lumana rejected the store's API key. The owner can replace it under Connector → Lumana Video.</p>
      ) : playable.length === 0 ? (
        <p className="qvlv-strip-msg">No camera is assigned to this order's register yet. Assign one under Connector → Lumana Video.</p>
      ) : (
        <div className="qvlv-reel">
          {playable.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`qvlv-frame qvlv-frame--${m.type.toLowerCase()}`}
              onClick={(e) => {
                openedFrom.current = e.currentTarget;
                setOpen(m.key);
              }}
            >
              <span className="qvlv-frame-img">
                {thumbs[m.key] ? <img src={thumbs[m.key]} alt="" /> : <span className={thumbs[m.key] === "" ? "qvlv-frame-none" : "qvlv-shimmer"} />}
                <span className="qvlv-frame-play">
                  <Play />
                </span>
              </span>
              <span className="qvlv-frame-cap">
                <b>{momentLabel(m)}</b>
                <small>{[fmtClock(m.at_ms, tz), momentCashier(m, data.order)].filter(Boolean).join(" · ")}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      {open && (
        <OrderVideoPlayer
          data={data}
          initialKey={open}
          onClose={() => {
            setOpen(null);
            openedFrom.current?.focus();
          }}
        />
      )}
    </section>
  );
};

export default OrderVideoStrip;
